import { z } from "zod";
import { EMAIL_TRIGGER_TYPES } from "@/types/email";
import { EMAIL_STATUS } from "./constants";

/**
 * Zod schemas for email validation
 */

// Email address validation
export const emailAddressSchema = z
  .string()
  .email("Invalid email address")
  .toLowerCase()
  .trim();

// Email trigger type validation
export const emailTriggerTypeSchema = z.enum([
  EMAIL_TRIGGER_TYPES.BOOKING_PRELIMINARY,
  EMAIL_TRIGGER_TYPES.BOOKING_CONFIRMED_CORPORATE,
  EMAIL_TRIGGER_TYPES.BOOKING_CONFIRMED_PRIVATE,
  EMAIL_TRIGGER_TYPES.BOOKING_CONFIRMED_ROADSHOW,
  EMAIL_TRIGGER_TYPES.BOOKING_UPDATED_MINOR,
  EMAIL_TRIGGER_TYPES.BOOKING_UPDATED_MAJOR,
  EMAIL_TRIGGER_TYPES.REMINDER_TIPS_CORPORATE,
  EMAIL_TRIGGER_TYPES.REMINDER_TIPS_PRIVATE,
  EMAIL_TRIGGER_TYPES.REMINDER_FINAL,
  EMAIL_TRIGGER_TYPES.POST_VISIT_REVIEW,
  EMAIL_TRIGGER_TYPES.ADDON_SPECIAL_QUESTIONS,
]);

// Email status validation
export const emailStatusSchema = z.enum([
  EMAIL_STATUS.PENDING,
  EMAIL_STATUS.SENT,
  EMAIL_STATUS.FAILED,
  EMAIL_STATUS.BOUNCED,
  EMAIL_STATUS.DELIVERED,
]);

// Email template creation schema
export const createEmailTemplateSchema = z.object({
  venue_id: z.number().positive("Venue ID must be positive"),
  show_id: z.number().positive().optional().nullable(),
  trigger_type: emailTriggerTypeSchema,
  name: z.string().min(1, "Template name is required").max(255),
  subject: z.string().min(1, "Subject is required"),
  body: z.string().min(1, "Body is required"),
  from_name: z.string().max(255).optional().nullable(),
  reply_to: emailAddressSchema.optional().nullable(),
  cc_emails: z.string().max(500).optional().nullable(),
  bcc_emails: z.string().max(500).optional().nullable(),
  status_id: z.number().positive(),
});

// Email template update schema
export const updateEmailTemplateSchema = createEmailTemplateSchema
  .partial()
  .extend({
    id: z.number().positive(),
  });

// Send email request schema
export const sendEmailRequestSchema = z.object({
  bookingId: z.number().positive("Booking ID must be positive"),
  triggerType: emailTriggerTypeSchema,
  customVariables: z.record(z.string()).optional(),
});

// Email preview request schema
export const previewEmailRequestSchema = z.object({
  templateId: z.number().positive("Template ID must be positive"),
  sampleData: z.record(z.string()).optional(),
});

// Email log filter schema
export const emailLogFilterSchema = z.object({
  venueId: z.number().positive().optional(),
  bookingId: z.number().positive().optional(),
  status: emailStatusSchema.optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  recipientEmail: emailAddressSchema.optional(),
});

// Email template filter schema
export const emailTemplateFilterSchema = z.object({
  venueId: z.number().positive().optional(),
  showId: z.number().positive().optional(),
  triggerType: emailTriggerTypeSchema.optional(),
  statusId: z.number().positive().optional(),
});

// Variable validation for templates
export const emailVariableSchema = z
  .object({
    // Customer variables
    customer_name: z.string().min(1),
    customer_email: emailAddressSchema,
    customer_phone: z.string().optional(),
    company_name: z.string().optional(),

    // Booking variables
    booking_number: z.string().min(1),
    booking_date: z.string().min(1),
    booking_time: z
      .string()
      .regex(/^\d{2}:\d{2}$/, "Time must be in HH:MM format"),
    participants: z.string().regex(/^\d+$/, "Participants must be a number"),
    booking_type: z.enum(["corporate", "private", "roadshow"]),

    // Show variables
    show_names: z.string().min(1),
    show_duration: z.string().optional(),
    min_charge: z.string().optional(),

    // Venue variables
    venue_name: z.string().min(1),
    venue_address: z.string().min(1),
    venue_phone: z.string().min(1),
    venue_email: emailAddressSchema,

    // Financial variables
    total_price: z.string().min(1),
    currency: z.string().min(1),

    // System variables
    portal_link: z.string().url(),
    cancel_link: z.string().url(),
    review_link: z.string().url().optional(),
    survey_link: z.string().url().optional(),
    cancellation_policy: z.string().min(1),

    // Dynamic variables for updates
    change_type: z.string().optional(),
    old_date: z.string().optional(),
    new_date: z.string().optional(),
    old_time: z.string().optional(),
    new_time: z.string().optional(),
  })
  .passthrough(); // Allow additional custom variables

// Partial schema for variable validation (only validates provided fields)
export const partialEmailVariableSchema = emailVariableSchema.partial();

/**
 * SECURITY: System-controlled variables that CANNOT be overridden via customVariables
 *
 * These variables are generated by the system and should never accept user input
 * to prevent phishing, price manipulation, and data integrity issues.
 *
 * Extracted from emailVariableSchema keys.
 */
export const SYSTEM_CONTROLLED_VARIABLES = new Set([
  // Customer variables (system-generated from database)
  "customer_name",
  "customer_email",
  "customer_phone",
  "company_name",

  // Booking variables (system-generated)
  "booking_number",
  "booking_date",
  "booking_time",
  "participants",
  "booking_type",

  // Show variables (system-generated)
  "show_names",
  "show_duration",
  "min_charge",

  // Venue variables (system-generated)
  "venue_name",
  "venue_address",
  "venue_phone",
  "venue_email",

  // Financial variables (system-generated)
  "total_price",
  "currency",
  "show_price",
  "addons_total",
  "total_amount",
  "base_amount",
  "vat_amount",
  "total_excl_vat",
  "total_incl_vat",
  "total_amount_inc_vat",
  "vat_rate",

  // System URLs (system-generated - CRITICAL for security)
  "portal_link",
  "cancel_link",
  "review_link",
  "survey_link",
  "cancellation_policy",

  // Add-on variables (admin-controlled HTML content)
  "special_addon_instructions", // Admin-generated HTML content for add-on instructions
  "special_questions", // Admin-generated HTML content for add-on questions
  "add_on_list", // System-generated list of add-ons with formatting
] as const);

/**
 * SECURITY: Validate custom variables don't override system-controlled variables
 *
 * This prevents attacks like:
 * - Phishing: Overriding portal_link with malicious URL
 * - Price manipulation: Changing total_price to show lower amount
 * - Identity spoofing: Changing customer_name/email
 *
 * @param customVariables - User-provided custom variables
 * @returns Validation result with error details if invalid
 */
export function validateCustomVariables(customVariables: Record<string, any>): {
  valid: boolean;
  invalidKeys?: string[];
  error?: string;
} {
  if (!customVariables || typeof customVariables !== "object") {
    return { valid: true };
  }

  const invalidKeys = Object.keys(customVariables).filter((key) =>
    SYSTEM_CONTROLLED_VARIABLES.has(key as any),
  );

  if (invalidKeys.length > 0) {
    return {
      valid: false,
      invalidKeys,
      error:
        `Cannot override system-controlled variables: ${invalidKeys.join(", ")}. ` +
        `Custom variables can only add new variables, not override existing ones.`,
    };
  }

  return { valid: true };
}

/**
 * Zod schema for custom variables validation
 * Ensures no system variables are overridden
 */
export const customVariablesSchema = z.record(z.any()).refine(
  (vars) => validateCustomVariables(vars).valid,
  (vars) => ({
    message: validateCustomVariables(vars).error || "Invalid custom variables",
  }),
);

// Webhook event validation
export const postmarkWebhookSchema = z.object({
  RecordType: z.enum([
    "Delivery",
    "Bounce",
    "SpamComplaint",
    "Open",
    "Click",
    "Unsubscribe",
  ]),
  MessageID: z.string(),
  Recipient: emailAddressSchema,
  Tag: z.string().optional(),
  DeliveredAt: z.string().optional(),
  BouncedAt: z.string().optional(),
  Details: z.string().optional(),
  Type: z.string().optional(),
  TypeCode: z.number().optional(),
  Metadata: z.record(z.any()).optional(),
});

// Helper function to validate and sanitize email data
export function validateEmailData<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
): { success: true; data: T } | { success: false; errors: z.ZodError } {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  return { success: false, errors: result.error };
}

// Export commonly used validation functions
export const validateEmailAddress = (email: string) =>
  validateEmailData(emailAddressSchema, email);

export const validateSendEmailRequest = (data: unknown) =>
  validateEmailData(sendEmailRequestSchema, data);

export const validateEmailTemplate = (data: unknown) =>
  validateEmailData(createEmailTemplateSchema, data);

export const validateEmailVariables = (data: unknown, partial = false) =>
  validateEmailData(
    partial ? partialEmailVariableSchema : emailVariableSchema,
    data,
  );
